import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Instagram, Twitter, Youtube, Globe, MapPin, Shield, Lock, ExternalLink, Copy, Languages, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import API from '@/lib/api';

export default function CreatorPublicProfile() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [creator, setCreator] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await API.get(`/creator/public/${publicId}`);
        setCreator(res.data.creator);
        setListings(res.data.listings || []);
      } catch (err) {
        setError(err.response?.data?.detail || 'Creator not found');
      } finally {
        setLoading(false);
      }
    };
    if (publicId) fetchProfile();
  }, [publicId]);

  const copyProfileLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Profile link copied!');
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F6F8FC] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F6F8FC] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl border border-[#D0D5DD] shadow-xl p-12 text-center max-w-md" data-testid="creator-not-found">
        <div className="w-16 h-16 rounded-full bg-[#F6F8FC] flex items-center justify-center mx-auto mb-4">
          <Shield className="w-8 h-8 text-[#475467]" />
        </div>
        <h1 className="font-heading text-2xl font-semibold text-[#101828] mb-2">Creator Not Found</h1>
        <p className="text-[#475467] text-sm mb-6">{error}</p>
        <Link to="/"><Button className="btn-primary">Go Home</Button></Link>
      </div>
    </div>
  );

  const socialIcons = { instagram: Instagram, twitter: Twitter, youtube: Youtube, tiktok: Globe, website: Globe };
  const scope = creator?.scope_posture || {};

  return (
    <div className="min-h-screen bg-[#F6F8FC]" data-testid="creator-public-profile">
      <nav className="bg-white border-b border-[#D0D5DD] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="font-heading text-xl font-semibold text-[#101828]">Mylikeness</Link>
          <Button onClick={() => navigate('/login')} variant="outline" className="border-[#D0D5DD] hover:bg-[#F6F8FC]">
            Sign In to Request
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Profile Header */}
        <div className="bg-white rounded-2xl border border-[#D0D5DD] shadow-sm overflow-hidden mb-8" data-testid="profile-header">
          <div className="h-32 bg-gradient-to-br from-[#EEF2FA] to-[#F6F8FC]" />
          <div className="px-8 pb-8">
            <div className="flex flex-col md:flex-row md:items-end gap-6 -mt-16">
              <Avatar className="w-32 h-32 border-4 border-white shadow-lg">
                <AvatarImage src={creator?.avatar_url} />
                <AvatarFallback className="bg-[#EEF2FA] text-[#2563EB] text-4xl font-semibold">{creator?.display_name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-heading text-2xl font-semibold text-[#101828]">{creator?.display_name}</h1>
                  {creator?.verification_status === 'verified' && (
                    <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200">
                      <CheckCircle className="w-3 h-3 mr-1" /> Verified
                    </Badge>
                  )}
                </div>
                <p className="text-[#475467] text-sm font-mono mb-3">{creator?.public_id}</p>
                {creator?.languages?.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-[#475467]">
                    <Languages className="w-4 h-4" />
                    {creator.languages.join(', ')}
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button onClick={copyProfileLink} variant="outline" className="border-[#D0D5DD] hover:bg-[#F6F8FC]" data-testid="share-profile-btn">
                  <Copy className="w-4 h-4 mr-2" /> Share
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            {/* Bio */}
            <div className="premium-card" data-testid="profile-bio">
              <h2 className="font-heading text-lg font-semibold text-[#101828] mb-4">About</h2>
              <p className="text-[#475467] leading-relaxed">{creator?.bio || 'No bio provided yet.'}</p>
            </div>

            {/* Scope Posture */}
            {(scope.supported_territories?.length > 0 || scope.supported_platforms?.length > 0) && (
              <div className="premium-card" data-testid="scope-posture">
                <h2 className="font-heading text-lg font-semibold text-[#101828] mb-4">Licensing Scope</h2>
                <div className="grid grid-cols-2 gap-6">
                  {scope.supported_territories?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-[#475467] text-sm mb-2">
                        <MapPin className="w-4 h-4" /> Territories
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {scope.supported_territories.map(t => (
                          <Badge key={t} variant="outline" className="bg-[#F6F8FC] border-[#D0D5DD] text-[#101828]">{t}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {scope.supported_platforms?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-[#475467] text-sm mb-2">
                        <Globe className="w-4 h-4" /> Platforms
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {scope.supported_platforms.map(p => (
                          <Badge key={p} variant="outline" className="bg-[#F6F8FC] border-[#D0D5DD] text-[#101828]">{p}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {scope.typical_term_days && (
                    <div>
                      <div className="flex items-center gap-2 text-[#475467] text-sm mb-2">
                        <Clock className="w-4 h-4" /> Typical Term
                      </div>
                      <p className="text-[#101828] font-medium">{scope.typical_term_days} days</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Likeness Pack Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-amber-800 mb-1">Full Likeness Pack</h3>
                  <p className="text-amber-700 text-sm leading-relaxed">
                    The complete likeness pack (high-resolution images, voice samples, and additional assets) is released only after a deal is finalized. Use a deal code to begin the process.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Social Links */}
            <div className="premium-card" data-testid="social-links">
              <h3 className="font-heading text-base font-semibold text-[#101828] mb-4">Connect</h3>
              <div className="space-y-3">
                {creator?.social_links && Object.entries(creator.social_links).map(([platform, link]) => {
                  if (!link) return null;
                  const Icon = socialIcons[platform] || Globe;
                  return (
                    <a key={platform} href={link.startsWith('http') ? link : `https://${link}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-[#F6F8FC] rounded-xl hover:bg-[#EEF2FA] transition-colors">
                      <Icon className="w-5 h-5 text-[#475467]" />
                      <span className="text-[#101828] text-sm font-medium capitalize">{platform}</span>
                      <ExternalLink className="w-3 h-3 text-[#475467] ml-auto" />
                    </a>
                  );
                })}
                {(!creator?.social_links || Object.values(creator.social_links).every(v => !v)) && (
                  <p className="text-[#475467] text-sm text-center py-4">No social links added</p>
                )}
              </div>
            </div>

            {/* Usage Info */}
            <div className="premium-card" data-testid="usage-preferences">
              <h3 className="font-heading text-base font-semibold text-[#101828] mb-4">Usage Preferences</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[#475467]">Endorsements</span>
                  <span className="text-[#101828] font-medium">Case by case</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475467]">AI Training</span>
                  <span className="text-red-600 font-medium">Not permitted</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#475467]">Approval Mode</span>
                  <span className="text-[#101828] font-medium">Manual review</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-[#2563EB] rounded-2xl p-6 text-white" data-testid="cta-card">
              <h3 className="font-heading text-base font-semibold mb-2">Ready to collaborate?</h3>
              <p className="text-blue-100 text-sm mb-4">Sign in to redeem a deal code from this creator.</p>
              <Button onClick={() => navigate('/login')} className="w-full bg-white text-[#2563EB] hover:bg-blue-50" data-testid="cta-get-started">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-[#D0D5DD] py-8 bg-white mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#475467] text-sm">
            Powered by <Link to="/" className="font-medium text-[#101828] hover:text-[#2563EB]">Mylikeness</Link> • Likeness management made simple
          </p>
        </div>
      </footer>
    </div>
  );
}
